import { observer } from "mobx-react";
import React, { FC, useCallback, useMemo, useState } from "react";
import { IntegrationConfiguratorLayout } from "../IntegrationConfiguratorLayout";
import {
  ApiMessageStack,
  IntegrationEntitySyncTag,
  SingleSelectInput,
} from "components";
import { Box, Button, Grid, GridItem, useToast } from "@chakra-ui/react";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { List } from "react-virtualized";
import { ReapitNegotiatorIntegrationSyncSelectOption } from "components/select-input/custom-options/ReapitNegotiatorIntegrationSyncSelectOption";
import { TSelectOption } from "types/select-input.type";
import { TReapitNegotiatorOptionData } from "types/reapit.type";

interface IProps {
  accountStore: UserAccountStore;
  isManuallyTriggered: boolean;
}

export const ReapitIntegrationDefaultNegotiatorConfigurator: FC<IProps> = observer(
  ({ accountStore, isManuallyTriggered }) => {
    const toast = useToast();
    const gridItemRef = React.useRef<HTMLDivElement>(null);

    const integrationsStore = accountStore.accountIntegrationsStore;
    const reapitIntegrationStore =
      integrationsStore.accountIntegrationReapitStore;
    const reapitNegotiatorsStore =
      reapitIntegrationStore.accountIntegrationReapitNegotiatorsStore;
    const defaultNegotiator = reapitNegotiatorsStore.reapitDefaultNegotiatorId;
    const setDefaultNegotiator =
      reapitNegotiatorsStore.setReapitDefaultNegotiatorId;
    const reapitNegotiators =
      reapitIntegrationStore.accountIntegrationReapitNegotiatorsStore
        .reapitNegotiatorsOptions;

    const [syncStatus, setSyncStatus] = useState(
      defaultNegotiator
        ? IntegrationEntitySyncStatusEnum.SYNCED
        : IntegrationEntitySyncStatusEnum.NOT_SYNCED
    );

    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectWidth = useMemo(() => {
      if (!gridItemRef.current) return 594;

      // Reduce by 2px to account for scrollbar
      return gridItemRef.current.offsetWidth - 2;
    }, [gridItemRef]);

    const optionsFilter = useCallback(
      (
        options: TSelectOption<string, TReapitNegotiatorOptionData>[],
        searchTerm: string
      ) => {
        return options.filter(option =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
      },
      []
    );

    const submitNewDefaultNegotiatorId = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await reapitNegotiatorsStore.updateAccountReapitDefaultNegotiatorId();
        await integrationsStore.fetchAccountIntegrations();
        if (defaultNegotiator) {
          setSyncStatus(IntegrationEntitySyncStatusEnum.SYNCED);
        }
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={"Reapit integration default negotiator updated."}
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [toast, reapitNegotiatorsStore, integrationsStore, defaultNegotiator]);

    return (
      <IntegrationConfiguratorLayout
        disabled={isManuallyTriggered}
        id="default-negotiator-mapping"
      >
        <Box fontSize={"xl"}>Default negotiator mapping</Box>
        <Box fontSize={"md"} color={"leadpro.400"}>
          Using the automatic Reapit integration requires a 'default negotiator'
          to be mapped which will be associated with all sync'd leads. This must
          be set individually for each office group when using the office group
          level integration.
        </Box>
        <Grid
          width={"full"}
          templateColumns={"repeat(3, 1fr)"}
          gridGap={2}
          alignItems={"center"}
        >
          <GridItem ref={gridItemRef} colSpan={2}>
            <SingleSelectInput
              options={reapitNegotiators}
              value={defaultNegotiator}
              clearable
              filterFn={optionsFilter}
              onChange={value => {
                setSyncStatus(IntegrationEntitySyncStatusEnum.NOT_SYNCED);
                setDefaultNegotiator(value);
              }}
              optionsRenderer={optionProps => (
                <List
                  rowHeight={55}
                  height={300}
                  rowCount={optionProps.length}
                  width={selectWidth}
                  rowRenderer={({ key, index, style }) => (
                    <div style={style} key={key}>
                      {optionProps[index] && (
                        <ReapitNegotiatorIntegrationSyncSelectOption
                          optionProps={optionProps[index]}
                        />
                      )}
                    </div>
                  )}
                />
              )}
            />
          </GridItem>
          <GridItem>
            <IntegrationEntitySyncTag syncStatus={syncStatus} />
          </GridItem>
        </Grid>
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitNewDefaultNegotiatorId}
            isLoading={isSubmitting}
            disabled={isManuallyTriggered}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
